import { useMemo, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PublicIcon from "@mui/icons-material/Public";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Each card is now a single click-to-select plan (no per-tier quantity
// steppers). Clicking a card selects it and locks in its fixed annual
// price — 1st card ₹10,000, 2nd ₹12,000, 3rd ₹15,000 — regardless of the
// individual AC/DC/No-Transaction rates shown for reference inside it.
const PLANS = [
  {
    id: "start",
    badge: "START",
    color: "#1f7a4d",
    tagline: "Kickstart Your Charging Business",
    audience: "For Hotels, Offices, RWAs, Small Fleets & New CPOs",
    chip: "Up to 10 Chargers",
    acRate: 5000,
    dcRate: 7000,
    noTxnRate: 10000,
    selectPrice: 5,
    icon: RocketLaunchOutlinedIcon,
  },
  {
    id: "grow",
    badge: "GROW",
    color: "#1355d6",
    tagline: "Grow and Run Operations Efficiently",
    audience: "For Growing CPOs, OEMs & Fleet Operators",
    chip: "11 – 50 Chargers",
    acRate: 4500,
    dcRate: 7000,
    noTxnRate: 10000,
    selectPrice: 10,
    icon: ShowChartOutlinedIcon,
  },
  {
    id: "enterprise",
    badge: "ENTERPRISE",
    color: "#7b2fd6",
    tagline: "Scale Without Limits",
    audience: "For Large CPOs, Oil Companies & Enterprise Networks",
    chip: "50+ Chargers",
    acRate: 4000,
    dcRate: 6000,
    noTxnRate: 15000,
    selectPrice: 15,
    icon: ApartmentOutlinedIcon,
  },
];

const ADDONS = [
  {
    icon: CurrencyRupeeIcon,
    title: "Transaction Fee",
    lines: ["₹ 1.00 – ₹ 1.50 / kWh", "(Upper limit ₹ 1.50 / kWh)", "(Lower limit ₹ 1.00 / kWh)"],
  },
  {
    icon: SellOutlinedIcon,
    title: "White-labelled CMS",
    subtitle: "(Lower Circuit)",
    lines: ["₹ 4,00,000", "+ 15% AMC"],
  },
  {
    icon: StarBorderIcon,
    title: "CMS – All Features Loaded",
    subtitle: "(Fleet + AI + Role Mgmt)",
    lines: ["₹ 6,00,000", "+ 15% AMC"],
  },
  {
    icon: PublicIcon,
    title: "Roaming",
    lines: ["₹ 1.00 / kWh"],
  },
  {
    icon: DonutLargeOutlinedIcon,
    title: "Dashboards Extra",
    subtitle: "(Annual up to 4 users)",
    lines: ["₹ 20,000", "per subscription"],
  },
  {
    icon: DnsOutlinedIcon,
    title: "Zynka ERP",
    subtitle: "One-time Setup Cost",
    lines: ["₹ 1,50,000", "+ Annual Charges", "₹ 2,000 per user per month"],
  },
];

const COMPLIANCE = [
  { icon: ShieldOutlinedIcon, text: "Enterprise Grade | Secure. Compliant. Reliable." },
  { icon: VerifiedOutlinedIcon, text: "OCPI, OCPP & Global Standards Compliant" },
  { icon: SpeedOutlinedIcon, text: "99.9% Platform Uptime Commitment" },
  { icon: LockOutlinedIcon, text: "Data Security & Privacy Compliant" },
];

function formatMoney(amount) {
  return `₹${amount.toLocaleString("en-IN")}`;
}

function PlanCard({ plan, selected, onSelect }) {
  const Icon = plan.icon;
  return (
    <Box
      onClick={() => onSelect(plan.id)}
      sx={{
        bgcolor: "#fff",
        color: "#000",
        borderRadius: "14px",
        p: 2.5,
        flex: 1,
        minWidth: 0,
        cursor: "pointer",
        border: selected ? "3px solid" : "1px solid",
        borderColor: selected ? plan.color : "#e0e0e0",
        transition: "border 0.15s ease",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Box
          sx={{
            bgcolor: plan.color,
            color: "#fff",
            fontWeight: 800,
            fontSize: 14,
            px: 1.75,
            py: 0.5,
            borderRadius: "8px",
          }}
        >
          {plan.badge}
        </Box>
        <Icon sx={{ color: plan.color, fontSize: 20 }} />
      </Stack>

      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#000", mb: 0.5 }}>
        {plan.tagline}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "#333", mb: 1 }}>{plan.audience}</Typography>

      <Box
        sx={{
          display: "inline-block",
          bgcolor: "#f0f0f0",
          color: "#000",
          fontSize: 11.5,
          fontWeight: 700,
          px: 1.25,
          py: 0.3,
          borderRadius: "6px",
          mb: 1.5,
        }}
      >
        {plan.chip}
      </Box>

      <Stack spacing={0.5} sx={{ borderTop: "1px solid #eee", pt: 1 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, color: "#000" }}>AC Charger Rate</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#000" }}>
            {formatMoney(plan.acRate)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, color: "#000" }}>DC Charger Rate</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#000" }}>
            {formatMoney(plan.dcRate)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, color: "#000" }}>No Transaction Plan</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#000" }}>
            {formatMoney(plan.noTxnRate)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function PlanSelectionStep({ onNext, onBack }) {
  const [selectedId, setSelectedId] = useState(null);

  const selectedPlan = useMemo(
    () => PLANS.find((p) => p.id === selectedId) ?? null,
    [selectedId]
  );

  const handleNext = () => {
    if (!selectedPlan) return;
    onNext({
      line_items: 
        {
          id: selectedPlan.id,
          label: selectedPlan.badge,
          plan: selectedPlan.id,
          quantity: 1,
          unit_price: selectedPlan.selectPrice,
          line_total: selectedPlan.selectPrice,
        },
      
      total_cost: selectedPlan.selectPrice,
      currency: "INR",
      duration: "Y",
    });
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", p: { xs: 2, md: 4 } }}>
      {/* Top header */}
      <Stack spacing={1} sx={{ mb: 3, color: "#000", textAlign: { xs: "left", md: "center" } }}>
        <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 800 }}>
          Flexible Plans. Powerful Platform. Built for Every Stage of Growth.
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#444" }}>
          Choose the plan that fits your business today. Scale with Zynka tomorrow.
        </Typography>
      </Stack>

      {/* Plan cards */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selected={selectedId === plan.id}
            onSelect={setSelectedId}
          />
        ))}
      </Stack>

      {/* Add-ons footer */}
      <Box sx={{ bgcolor: "#f5f5f5", borderRadius: "12px", mt: 2, p: 2.5 }}>
        <Typography sx={{ color: "#000", fontWeight: 700, fontSize: 13, letterSpacing: 0.4, mb: 2 }}>
          COMMON ADD-ONS & COMMERCIALS
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {ADDONS.map((addon) => {
            const Icon = addon.icon;
            return (
              <Stack key={addon.title} direction="row" spacing={1} sx={{ flex: 1 }}>
                <Icon sx={{ color: "#000", fontSize: 20, mt: "2px" }} />
                <Box>
                  <Typography sx={{ color: "#000", fontSize: 12.5, fontWeight: 700 }}>
                    {addon.title}
                  </Typography>
                  {addon.subtitle && (
                    <Typography sx={{ color: "#555", fontSize: 11 }}>{addon.subtitle}</Typography>
                  )}
                  {addon.lines.map((line, i) => (
                    <Typography
                      key={line}
                      sx={{
                        color: "#000",
                        fontSize: 11.5,
                        fontWeight: i === 0 ? 700 : 400,
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      {/* Compliance bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.5, md: 4 }}
        justifyContent="center"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ borderTop: "1px solid #eee", mt: 2, pt: 2, px: 1 }}
      >
        {COMPLIANCE.map((item) => {
          const Icon = item.icon;
          return (
            <Stack key={item.text} direction="row" spacing={0.75} alignItems="center">
              <Icon sx={{ color: "#000", fontSize: 16 }} />
              <Typography sx={{ color: "#000", fontSize: 12 }}>{item.text}</Typography>
            </Stack>
          );
        })}
      </Stack>
      <Typography sx={{ textAlign: "center", color: "#666", fontSize: 11, mt: 1.5 }}>
        All prices are in INR. Taxes as applicable. Terms & conditions apply.
      </Typography>

      {/* Selection summary + nav */}
      {selectedPlan && (
        <Box sx={{ bgcolor: "#f5f5f5", borderRadius: "12px", mt: 2, p: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: "#000", fontSize: 13 }}>
              {selectedPlan.badge} plan selected
            </Typography>
            <Typography sx={{ color: "#000", fontWeight: 700, fontSize: 13 }}>
              {formatMoney(selectedPlan.selectPrice)}/year
            </Typography>
          </Stack>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button onClick={onBack} sx={{ color: "#666" }}>
          Back
        </Button>
        <Button variant="contained" disabled={!selectedPlan} onClick={handleNext}>
          Continue
        </Button>
      </Stack>
    </Box>
  );
}